const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Service for handling project-related API calls with the new hierarchical structure
 */
export class ProjectService {
  
  /**
   * Get all projects for a user with collection counts
   */
  static async getUserProjects(userId, tagId = null) {
    try {
      let url = `${API_BASE_URL}/api/projects?userId=${userId}`;
      
      if (tagId) {
        url += `&tagId=${tagId}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  }

  /**
   * Get a single project by ID
   */
  static async getProjectById(projectId, userId) {
    try {
      const url = `${API_BASE_URL}/api/projects/${projectId}?userId=${userId}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch project: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  }

  /**
   * Get project collections with pagination - OPTIMIZED for large projects
   */
  static async getProjectCollectionsPaginated(projectId, userId, page = 1, limit = 50, search = '', bustCache = false) {
    try {
      const params = new URLSearchParams({
        userId,
        page: page.toString(),
        limit: limit.toString(),
        search
      });

      // Add cache-busting timestamp if needed (for force refresh after mutations)
      if (bustCache) {
        params.append('_t', Date.now().toString());
        console.log(`🔓 Cache busting enabled for request`);
      }

      const url = `${API_BASE_URL}/api/collections/projects/${projectId}/collections-paginated?${params}`;

      const response = await fetch(url, {
        // Disable browser cache for this request
        cache: bustCache ? 'no-store' : 'default',
        headers: bustCache ? {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        } : {}
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch project collections: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching project collections:', error);
      throw error;
    }
  }

  /**
   * Get project details with full hierarchy (collections + root files) - DEPRECATED
   * Use getProjectCollectionsPaginated instead for better performance with large datasets
   */
  static async getProjectHierarchy(projectId, userId) {
    try {
      const url = `${API_BASE_URL}/api/collections/projects/${projectId}/hierarchy?userId=${userId}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch project hierarchy: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching project hierarchy:', error);
      throw error;
    }
  }

  /**
   * Get all collections for a project
   */
  static async getProjectCollections(projectId, userId) {
    try {
      const url = `${API_BASE_URL}/api/collections/projects/${projectId}/collections?userId=${userId}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch project collections: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching project collections:', error);
      throw error;
    }
  }

  /**
   * Get collection files with pagination - OPTIMIZED for large collections
   */
  static async getCollectionFilesPaginated(collectionId, userId, page = 1, limit = 50, search = '') {
    try {
      const params = new URLSearchParams({
        userId,
        page: page.toString(),
        limit: limit.toString(),
        search
      });
      
      const url = `${API_BASE_URL}/api/collections/${collectionId}/files-paginated?${params}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch collection files: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching collection files:', error);
      throw error;
    }
  }

  /**
   * Get collection details with files - DEPRECATED for large collections
   * Use getCollectionFilesPaginated instead for better performance
   */
  static async getCollectionDetails(collectionId, userId) {
    try {
      const url = `${API_BASE_URL}/api/collections/${collectionId}?userId=${userId}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch collection details: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching collection details:', error);
      throw error;
    }
  }

  /**
   * Create a new collection in a project
   */
  static async createCollection(projectId, userId, collectionData) {
    try {
      const url = `${API_BASE_URL}/api/collections/projects/${projectId}/collections`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          ...collectionData
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create collection: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating collection:', error);
      throw error;
    }
  }

  /**
   * Update collection (rename, description, tags)
   */
  static async updateCollection(collectionId, userId, updateData) {
    try {
      const url = `${API_BASE_URL}/api/collections/${collectionId}`;
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          ...updateData
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update collection: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating collection:', error);
      throw error;
    }
  }

  /**
   * Delete collection and all its files
   */
  static async deleteCollection(collectionId, userId) {
    try {
      const url = `${API_BASE_URL}/api/collections/${collectionId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete collection: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting collection:', error);
      throw error;
    }
  }

  /**
   * Move files between collections or to/from root
   */
  static async moveFiles(collectionId, userId, fileIds, targetCollectionId = null) {
    try {
      const url = `${API_BASE_URL}/api/collections/${collectionId}/move-files`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          fileIds,
          targetCollectionId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to move files: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error moving files:', error);
      throw error;
    }
  }

  /**
   * Legacy compatibility: Rename project (still works with new structure)
   */
  static async renameProject(projectId, newName) {
    try {
      const url = `${API_BASE_URL}/api/projects/${projectId}/rename`;
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newName: newName.trim() })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to rename project');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error renaming project:', error);
      throw error;
    }
  }

  /**
   * Legacy compatibility: Delete project (still works with new structure)
   */
  static async deleteProject(projectId) {
    try {
      const url = `${API_BASE_URL}/api/projects/${projectId}`;
      
      const response = await fetch(url, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete project');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }

  /**
   * Delete a single file (with S3 and database cleanup)
   */
  static async deleteFile(fileId, userId) {
    try {
      const url = `${API_BASE_URL}/api/files/${fileId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete file');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }
}